import React from "react";
import { useTheme, Headline, Modal } from "react-native-paper";
import { View, StyleSheet, Image } from "react-native";
import { ThemeType } from "../../theme/theme";
import { Text, Button } from "../../components";
import { testProps } from "../../utils/testProps";
import { translate } from "../../i18n/translate";
import { get } from "lodash";
import { TextHighlightWords } from "../../components/text/text-highlight-words";

interface ConfirmationModalProps {
    visible: boolean;
    setVisible: any;
    isLoading: boolean;
    confirmCallback?: any;
    denyCallback?: any;
    onDismissCallback?: any;
    bodyTextClickCallback?: any;
    headLineText?: string;
    headLineTextStyle?: any;
    headLineIcon?:string;
    bodyText?: string;
    bodyTextStyle?: any;
    buttonConfirmText: string;
    buttonConfirmStyle?:  any;
    buttonConfirmTextStyle?: any;
    buttonCancelText?: string;
    buttonCancelStyle?:  any;
    buttonCancelTextStyle?: any;
    hideCancelButton?: boolean;
    useBodyTextHilight?: boolean;
    hightlightColor?:string;
    wordsToHighlight?: Array<string>;
  }

export const ConfirmationModal = (props: ConfirmationModalProps) => {
  const theme = useTheme() as ThemeType;
  const styles = getStyles(theme);
  const { colors } = theme;
  let iconUri;
  let {
    visible,
    setVisible,
    isLoading,
    confirmCallback,
    denyCallback,
    onDismissCallback,
    bodyTextClickCallback,
    headLineText,
    headLineTextStyle,
    bodyText,
    bodyTextStyle,
    buttonConfirmText,
    buttonCancelText,
    buttonConfirmStyle,
    buttonCancelStyle,
    buttonConfirmTextStyle,
    buttonCancelTextStyle,
    headLineIcon,
    hideCancelButton,
    hightlightColor,
    wordsToHighlight,
  } = props;

  if(!hideCancelButton) {
    hideCancelButton = false;
  }

  if(!buttonCancelText) {
    //Use default 'Cancel' text
    const defaultText = translate("componentStatic.cancel");
    buttonCancelText = defaultText ? defaultText : "";
  }

  if(!buttonCancelStyle) {
    //use default button with white background.
    buttonCancelStyle = {
      backgroundColor: colors.white,
      borderWidth: 1,
      borderColor: colors.darkBg,
    }
  }

  if(headLineIcon){
    //get uri of the icon image to be used
    //'require' doesn't allow to be constructed dynamically
    iconUri = get(Modal_Icons, headLineIcon).uri
  }

  function handleNoButton() {
    setVisible(false);
    if(denyCallback){
      denyCallback();
    }
  }

  function handleOnDismiss() {
    setVisible(false);
    if(onDismissCallback) {
      onDismissCallback();
    }
  }

  return (
    <Modal visible={visible} onDismiss={handleOnDismiss} contentContainerStyle={styles.modalContainer}>
      <View style={styles.outerContainer}>
          { headLineText &&
           <View style={styles.rowContainer} accessible={true}>
            <Headline style={{...styles.headlineStyle, ...headLineTextStyle}}>
              { headLineIcon && <Image
                style={styles.headLineIconStyle}
                source={iconUri}
              />
              } 
                {headLineText}
            </Headline>
            </View>
          }
          { bodyText && 
           <View style={styles.rowContainer} accessible={true}>
            {wordsToHighlight ?
              <TextHighlightWords 
                text={bodyText}
                style={{...styles.textStyle, ...bodyTextStyle}} 
                hightlightColor={hightlightColor}
                wordsToHighlight={wordsToHighlight as Array<string>} 
                onPress={bodyTextClickCallback}
              />
            :
              <Text
                style={{...styles.textStyle, ...bodyTextStyle}}
                onPress={bodyTextClickCallback}
              >
                {bodyText}
              </Text>
            }
            </View>
          }
        <View style={styles.buttonRowContainer} accessible={true}>
          {!hideCancelButton &&
            <Button
              style={{...buttonCancelStyle }}
              mode="contained"
              text={buttonCancelText}
              disabled={isLoading}            
              onPress={handleNoButton}
              textStyle = {buttonCancelTextStyle}
              {...testProps("modal-no-button")}
            />
          }
          <Button
            style={{...buttonConfirmStyle }}
            mode="contained"
            text={buttonConfirmText}
            disabled={isLoading}
            loading={isLoading}
            onPress={confirmCallback}
            textStyle = {buttonConfirmTextStyle}
            {...testProps("modal-yes-button")}
          />
        </View>
      </View>
    </Modal>
  );
};

const getStyles = (theme: ThemeType) => {
  const { colors, spacing, fontSize, iconSize } = theme;
  return StyleSheet.create({
    modalContainer: {
      display: "flex",
      marginHorizontal: spacing.medium,
      backgroundColor: colors.background,
      borderRadius: 10,
      flexDirection: "column",
    },
    outerContainer: {
      display: "flex",
      padding: spacing.medium,
      flexDirection: "column",
      justifyContent: "space-between",
    },
    rowContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: 'center', //Centered vertically
      marginTop:spacing.large,
    },
    buttonRowContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-around",
      marginTop:spacing.huge  
    },
    textStyle: {      
      fontSize: fontSize.large,
    },
    headlineStyle: {
      fontSize: fontSize.large,
      fontWeight: "bold",
    },
    headLineIconStyle: {
      width: iconSize.small,
      height: iconSize.small,
    },
  });
}

//Allows modal icon to be swappable
const Modal_Icons = {
  exclamation: { 
    uri: require('../../../assets/exclamation.png')
  }
}